import { ZodError, type z } from 'zod';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
const TIMEOUT_MS = 30_000;

/**
 * Fired when the session is gone for good, after a refresh has already been
 * tried. The app listens once, at the root, rather than every hook handling it.
 */
export const AUTH_EXPIRED_EVENT = 'ponyup:auth-expired';

export class ApiError extends Error {
    public readonly status: number;

    constructor(status: number, message: string) {
        super(message);
        this.status = status;
        this.name = 'ApiError';
    }
}

export const getErrorMessage = (error: unknown): string => {
    if (error instanceof ApiError) return error.message;
    // A ZodError's message is its whole issue list as JSON, which is not
    // something to show a person mid-bet.
    if (error instanceof ZodError) {
        return error.issues[0]?.message ?? 'Check the values you entered';
    }
    if (error instanceof Error) return error.message;
    return 'Something went wrong';
};

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions {
    /** Skips the refresh-and-replay path. Used by the refresh call itself. */
    noRetry?: boolean;
}

const send = async (method: Method, path: string, body?: unknown): Promise<Response> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
        return await fetch(`${BASE}/${path.replace(/^\//, '')}`, {
            method,
            // Auth rides on httpOnly cookies, so every call must carry them.
            credentials: 'include',
            headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
            body: body === undefined ? undefined : JSON.stringify(body),
            signal: controller.signal,
        });
    } finally {
        clearTimeout(timeout);
    }
};

const messageFrom = async (response: Response): Promise<string> => {
    try {
        const payload: unknown = await response.json();
        if (
            typeof payload === 'object' &&
            payload !== null &&
            'message' in payload &&
            typeof payload.message === 'string'
        ) {
            return payload.message;
        }
    } catch {
        // A non-JSON error body is normal for proxy-level failures.
    }
    return response.statusText || 'Request failed';
};

// One refresh in flight at a time. Several queries failing together would
// otherwise each rotate the refresh token, and all but one would lose the race
// and sign the user out.
let refreshInFlight: Promise<boolean> | null = null;

const refreshSession = async (): Promise<boolean> => {
    refreshInFlight ??= (async () => {
        try {
            const response = await send('POST', 'auth/refresh');
            return response.ok;
        } catch {
            return false;
        } finally {
            // Cleared after the current callers settle so the next failure
            // starts a fresh attempt.
            setTimeout(() => {
                refreshInFlight = null;
            }, 0);
        }
    })();

    return refreshInFlight;
};

const request = async <T>(
    method: Method,
    path: string,
    body?: unknown,
    responseSchema?: z.ZodType<T>,
    options?: RequestOptions,
): Promise<T> => {
    let response = await send(method, path, body);

    if ((response.status === 401 || response.status === 403) && !options?.noRetry) {
        const refreshed = await refreshSession();
        if (refreshed) {
            response = await send(method, path, body);
        }

        if (!refreshed || response.status === 401) {
            window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
            throw new ApiError(401, 'Your session expired. Sign in again.');
        }
    }

    if (!response.ok) throw new ApiError(response.status, await messageFrom(response));

    if (response.status === 204) return undefined as T;

    const payload: unknown = await response.json();
    if (!responseSchema) return payload as T;

    const parsed = responseSchema.safeParse(payload);
    if (!parsed.success) {
        // A shape mismatch is a contract break between client and server, not
        // a user error, so it says so rather than blaming the request.
        throw new ApiError(500, `Unexpected response shape from ${path}`);
    }
    return parsed.data;
};

export const apiClient = {
    get: <T>(path: string, schema?: z.ZodType<T>): Promise<T> =>
        request('GET', path, undefined, schema),

    post: <T>(
        path: string,
        body?: unknown,
        schema?: z.ZodType<T>,
        options?: RequestOptions,
    ): Promise<T> => request('POST', path, body, schema, options),

    patch: <T>(path: string, body?: unknown, schema?: z.ZodType<T>): Promise<T> =>
        request('PATCH', path, body, schema),

    delete: <T>(path: string, schema?: z.ZodType<T>): Promise<T> =>
        request('DELETE', path, undefined, schema),
};
